import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, UploadCloud, Sparkles, Save, CheckCircle2, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { uploadBvaFile, startBvaCompute, fetchBvaComputeJob, startBvaNote, fetchBvaNoteJob, saveBvaNote, fetchBva } from "@/lib/cashrj";

const eur = (v) => `${(Number(v) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const DROPS = [
    { type: "releve", label: "Relevé bancaire du mois", hint: "Flux de trésorerie (Débit/Crédit ou Montant signé)" },
    { type: "achats", label: "Factures d'Achats de la période", hint: "Montant HT" },
    { type: "ventes", label: "Factures de Vente (CA) de la période", hint: "Montant HT" },
];
const ROWS = [
    ["encaissements", "Encaissements (trésorerie)"],
    ["decaissements", "Décaissements (trésorerie)"],
    ["ca", "Chiffre d'affaires facturé (HT)"],
    ["achats", "Achats facturés (HT)"],
];

export const BvaReporting = ({ dossierId, mois, annee, scenarios }) => {
    const [scenarioId, setScenarioId] = useState("");
    const [files, setFiles] = useState({});
    const [uploading, setUploading] = useState("");
    const [computing, setComputing] = useState(false);
    const [report, setReport] = useState(null);
    const [note, setNote] = useState("");
    const [noteStage, setNoteStage] = useState(null);
    const [savingNote, setSavingNote] = useState(false);
    const poll = useRef(null);
    const noteP = useRef(null);
    const latest = useRef("");

    useEffect(() => { setScenarioId((c) => c || (scenarios[0] && scenarios[0].id) || ""); }, [scenarios]);

    const loadExisting = useCallback(async () => {
        const key = `${mois}-${annee}`;
        latest.current = key;
        try {
            const r = await fetchBva(dossierId, mois, annee);
            if (latest.current !== key) return; // réponse périmée ignorée (anti-race au reload)
            if (r) {
                setReport(r);
                setNote(r.note_markdown || "");
                if (r.previsionnel_id) setScenarioId(r.previsionnel_id);
                setFiles(Object.fromEntries((r.sources || []).map((s) => [s.type, s])));
            } else { setReport(null); setNote(""); setFiles({}); }
        } catch { /* table non migrée */ }
    }, [dossierId, mois, annee]);

    useEffect(() => { loadExisting(); }, [loadExisting]);
    useEffect(() => () => { [poll.current, noteP.current].forEach((p) => p && clearInterval(p)); }, []);

    async function onFile(type, file) {
        if (!file) return;
        setUploading(type);
        try {
            const r = await uploadBvaFile(dossierId, type, file);
            setFiles((f) => ({ ...f, [type]: r }));
            toast.success(`${r.nom_fichier} importé`);
        } catch (e) { toast.error("Import impossible", { description: e.message }); }
        finally { setUploading(""); }
    }

    async function compute() {
        const sources = Object.values(files).filter(Boolean);
        if (!sources.length) { toast.error("Importez au moins un fichier (relevé, achats ou ventes)."); return; }
        setComputing(true);
        try {
            const { job_id } = await startBvaCompute(dossierId, { mois, annee, previsionnel_id: scenarioId || null, sources });
            if (poll.current) clearInterval(poll.current);
            await new Promise((resolve, reject) => {
                let n = 0;
                poll.current = setInterval(async () => {
                    if (++n > 90) { clearInterval(poll.current); reject(new Error("Délai dépassé")); return; }
                    try {
                        const j = await fetchBvaComputeJob(dossierId, job_id);
                        if (j.stage === "done") { clearInterval(poll.current); resolve(j); }
                        else if (j.stage === "error") { clearInterval(poll.current); reject(new Error(j.error || "Échec du calcul")); }
                    } catch (e) { clearInterval(poll.current); reject(e); }
                }, 2000);
            });
            await loadExisting();
            toast.success("Budget vs Réalisé calculé");
        } catch (e) { toast.error("Calcul impossible", { description: e.message }); }
        finally { setComputing(false); }
    }

    async function writeNote() {
        if (!report?.id) { toast.error("Calculez d'abord le Budget vs Réalisé."); return; }
        setNoteStage("redaction");
        try {
            const { job_id } = await startBvaNote(dossierId, report.id);
            if (noteP.current) clearInterval(noteP.current);
            const res = await new Promise((resolve, reject) => {
                let n = 0;
                noteP.current = setInterval(async () => {
                    if (++n > 90) { clearInterval(noteP.current); reject(new Error("Délai dépassé")); return; }
                    try {
                        const j = await fetchBvaNoteJob(dossierId, job_id);
                        if (j.stage === "done") { clearInterval(noteP.current); resolve(j); }
                        else if (j.stage === "error") { clearInterval(noteP.current); reject(new Error(j.error || "Échec")); }
                    } catch (e) { clearInterval(noteP.current); reject(e); }
                }, 2500);
            });
            setNote(res.result?.note_markdown || "");
            toast.success("Note de gestion rédigée (IA)");
        } catch (e) { toast.error("Rédaction impossible", { description: e.message }); }
        finally { setNoteStage(null); }
    }

    async function persistNote() {
        if (!report?.id) return;
        setSavingNote(true);
        try { await saveBvaNote(dossierId, report.id, note); toast.success("Note enregistrée"); }
        catch (e) { toast.error("Enregistrement impossible", { description: e.message }); }
        finally { setSavingNote(false); }
    }

    const ec = report?.ecarts || {};
    const prevu = report?.prevu || {};
    const realise = report?.realise || {};

    return (
        <div data-testid="cash-rj-bva" className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-slate-500">Prévisionnel de référence :</span>
                <Select value={scenarioId} onValueChange={setScenarioId}>
                    <SelectTrigger data-testid="bva-scenario" className="h-9 w-[240px] max-w-[280px] bg-white text-sm"><SelectValue placeholder="Scénario budget" /></SelectTrigger>
                    <SelectContent>
                        {scenarios.length === 0 && <SelectItem value="none" disabled>Aucun scénario</SelectItem>}
                        {scenarios.map((s) => <SelectItem key={s.id} value={s.id}>{s.titre || "Sans titre"}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
                {DROPS.map((d) => (
                    <label key={d.type} data-testid={`bva-drop-${d.type}`} className="flex cursor-pointer flex-col gap-1 rounded-xl border border-dashed border-slate-300 bg-white p-3 text-center hover:border-emerald-400 hover:bg-emerald-50/40 transition-colors">
                        <input type="file" accept=".xlsx,.xls,.csv" className="hidden" data-testid={`bva-input-${d.type}`} onChange={(e) => onFile(d.type, e.target.files?.[0])} />
                        <div className="flex items-center justify-center gap-1.5 text-emerald-700">
                            {uploading === d.type ? <Loader2 className="h-4 w-4 animate-spin" /> : files[d.type] ? <CheckCircle2 className="h-4 w-4" /> : <UploadCloud className="h-4 w-4" />}
                            <span className="text-xs font-semibold text-slate-800">{d.label}</span>
                        </div>
                        <span className="text-[11px] text-slate-500 truncate">{files[d.type]?.nom_fichier || d.hint}</span>
                    </label>
                ))}
            </div>

            <Button type="button" data-testid="bva-compute-btn" onClick={compute} disabled={computing} className="rounded-full bg-slate-800 hover:bg-slate-900 text-white">
                {computing ? <><Loader2 className="h-4 w-4 animate-spin" /> Analyse Pandas…</> : <><FileSpreadsheet className="h-4 w-4" /> Calculer le Budget vs Réalisé</>}
            </Button>

            {report && (
                <div className="overflow-x-auto rounded-xl border border-slate-200" data-testid="bva-table">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                            <tr><th className="py-2 px-3 text-left">Item</th><th className="py-2 px-3 text-right">Prévu</th><th className="py-2 px-3 text-right">Réalisé</th><th className="py-2 px-3 text-right">Écart €</th><th className="py-2 px-3 text-right">Écart %</th></tr>
                        </thead>
                        <tbody>
                            {ROWS.map(([k, lab]) => {
                                const e = ec[k] || {};
                                const cost = k === "decaissements" || k === "achats";
                                const favorable = cost ? Number(e.ecart_eur) <= 0 : Number(e.ecart_eur) >= 0;
                                const cls = favorable ? "text-emerald-600" : "text-rose-600";
                                return (
                                    <tr key={k} className="border-t border-slate-100" data-testid={`bva-row-${k}`}>
                                        <td className="py-2 px-3 text-left">{lab}</td>
                                        <td className="py-2 px-3 text-right tabular-nums">{eur(prevu[k])}</td>
                                        <td className="py-2 px-3 text-right tabular-nums">{eur(realise[k])}</td>
                                        <td className={`py-2 px-3 text-right tabular-nums font-medium ${cls}`}>{eur(e.ecart_eur)}</td>
                                        <td className={`py-2 px-3 text-right tabular-nums ${cls}`}>{e.ecart_pct == null ? "—" : `${e.ecart_pct > 0 ? "+" : ""}${e.ecart_pct}%`}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {report && (
                <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                        <Button type="button" data-testid="bva-note-btn" onClick={writeNote} disabled={!!noteStage} variant="outline" className="rounded-full">
                            {noteStage ? <><Loader2 className="h-4 w-4 animate-spin" /> Rédaction IA…</> : <><Sparkles className="h-4 w-4" /> Rédiger la note de gestion (IA)</>}
                        </Button>
                        <Button type="button" data-testid="bva-note-save" onClick={persistNote} disabled={savingNote || !note} variant="outline" className="rounded-full">
                            {savingNote ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Enregistrer la note
                        </Button>
                    </div>
                    <Textarea data-testid="bva-note-editor" value={note} onChange={(e) => setNote(e.target.value)} rows={10} placeholder="La note de gestion (Markdown) apparaîtra ici. Vous pouvez la modifier avant de générer le Rapport de Gestion (Export 2)." className="font-mono text-xs bg-white" />
                </div>
            )}
        </div>
    );
};
